import { useCallback, useEffect, useRef, useState } from 'react';
import { liveSocketUrl } from './api/client.js';
import { LiveConnection, type LiveStatus } from './api/live.js';
import type { LiveValue } from './api/types.js';
import { EMPTY_LIVE_STATE, mergeLive, type LiveState } from './lib/live.js';

/** `value`, updated only after it has been stable for `ms`. */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [value, ms]);
  return debounced;
}

/** Current time, re-rendering every `intervalMs` (for "12 s ago"). */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(t);
    };
  }, [intervalMs]);
  return now;
}

export interface Resource<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

/**
 * Load data with `load(signal)`; reloads when `key` changes and, with
 * `refreshMs`, periodically (keeping the old data on screen meanwhile).
 */
export function useResource<T>(
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
  refreshMs?: number,
): Resource<T> {
  const [tick, setTick] = useState(0);
  const request = `${key}#${tick}`;
  const [state, setState] = useState<{ key: string; request: string; data?: T; error?: Error }>({
    key: '',
    request: '',
  });
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const ctrl = new AbortController();
    loadRef.current(ctrl.signal).then(
      (data) => {
        if (!ctrl.signal.aborted) setState({ key, request, data });
      },
      (err: unknown) => {
        if (ctrl.signal.aborted) return;
        const error = err instanceof Error ? err : new Error(String(err));
        // Keep data of the same key on screen next to the error.
        setState((s) => ({ ...(s.key === key ? s : {}), key, request, error }));
      },
    );
    return () => {
      ctrl.abort();
    };
  }, [key, request]);

  useEffect(() => {
    if (!refreshMs) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') setTick((n) => n + 1);
    }, refreshMs);
    return () => {
      clearInterval(t);
    };
  }, [refreshMs, key]);

  const reload = useCallback(() => {
    setTick((n) => n + 1);
  }, []);
  const current = state.key === key;
  return {
    data: current ? state.data : undefined,
    error: current ? state.error : undefined,
    loading: state.request !== request,
    reload,
  };
}

/**
 * Live values pushed for `ids` over one WebSocket (older ones ignored).
 * `baseline` holds the values from REST responses, so the first push that
 * changes a value flashes too. Resubscribes when the id set changes.
 */
export function useLiveValues(
  ids: readonly string[],
  baseline: readonly (LiveValue | null)[],
  onValues?: (values: LiveValue[]) => void,
): { live: LiveState; status: LiveStatus } {
  const [live, setLive] = useState<LiveState>(EMPTY_LIVE_STATE);
  const [status, setStatus] = useState<LiveStatus>('idle');
  const connRef = useRef<LiveConnection | null>(null);
  const onValuesRef = useRef(onValues);
  useEffect(() => {
    onValuesRef.current = onValues;
  });

  const baselineRef = useRef(new Map<string, LiveValue>());
  useEffect(() => {
    baselineRef.current = new Map(baseline.flatMap((v) => (v ? [[v.point_id, v] as const] : [])));
  }, [baseline]);

  useEffect(() => {
    const conn = new LiveConnection({
      url: (points) => liveSocketUrl(points, window.location),
      onValues: (values) => {
        setLive((s) => mergeLive(s, values, (id) => baselineRef.current.get(id)));
        onValuesRef.current?.(values);
      },
      onStatus: setStatus,
      batchMs: 200,
    });
    connRef.current = conn;
    return () => {
      conn.close();
      connRef.current = null;
    };
  }, []);

  const key = [...ids].sort().join(',');
  useEffect(() => {
    connRef.current?.setPoints(key ? key.split(',') : []);
  }, [key]);

  return { live, status };
}
